import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { ImageUploader } from "@/components/ImageUploader";
import { VideoUploader } from "@/components/VideoUploader";
import PlacesAutocompleteInput from "@/components/PlacesAutocompleteInput";
import { US_STATES, VEHICLE_BRANDS, TRANSMISSIONS, FUEL_TYPES, CONDITIONS } from "@/lib/listings";
import { ArrowRight, Save } from "lucide-react";
import { toast } from "sonner";

export default function PostVehicle() {
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    listing_type: "sale",
    brand: "Toyota",
    model: "",
    year: new Date().getFullYear(),
    color: "",
    mileage: 0,
    transmission: "automatic",
    fuel_type: "gasoline",
    condition: "used",
    price: "",
    state: "",
    city: "",
    description: "",
    owner_name: user?.name || "",
    phone: user?.phone || "",
    images: [],
    videos: [],
    accept_terms: false,
  });

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, owner_name: f.owner_name || user.name || "", phone: f.phone || user.phone || "" }));
  }, [user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["title", "brand", "model", "color", "price", "state", "city", "description", "owner_name", "phone"];
    if (required.some((k) => !form[k])) {
      toast.error(t("يرجى تعبئة كل الحقول المطلوبة", "Please fill required fields"));
      return;
    }
    if (!form.accept_terms) {
      toast.error(t("يجب الموافقة على الشروط والأحكام قبل نشر الإعلان.", "You must accept the Terms and Conditions before publishing."));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: parseInt(form.year),
        mileage: parseInt(form.mileage) || 0,
        price: parseFloat(form.price),
        accept_terms: true,
      };
      const createResp = await axios.post(`${API}/vehicles`, payload, { headers: { Authorization: `Bearer ${token}` } });
      // Register every uploaded image with the moderation pipeline now that we have a vehicle_id.
      const newId = createResp.data.vehicle_id;
      if (newId && (form.images || []).length > 0) {
        await Promise.all(form.images.map((path) =>
          axios.post(`${API}/media`, {
            listing_type: "vehicle", listing_id: newId, url: path, media_type: "image",
          }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
        ));
      }
      if (newId && (form.videos || []).length > 0) {
        await Promise.all(form.videos.map((path) =>
          axios.post(`${API}/media`, {
            listing_type: "vehicle", listing_id: newId, url: path, media_type: "video",
          }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {})
        ));
      }
      toast.success(t("تم إرسال إعلان السيارة للمراجعة", "Vehicle submitted for review"));
      navigate("/vehicles/mine");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message_ar || detail?.message_en || t("فشل النشر", "Failed to post"));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-yellow-500 outline-none";

  return (
    <DashboardLayout title={t("نشر سيارة", "Post Vehicle")}>
      <form onSubmit={submit} className="max-w-4xl space-y-6" data-testid="post-vehicle-form">
        <Link to="/vehicles" className="inline-flex items-center gap-2 text-yellow-500 hover:underline">
          <ArrowRight size={16} /> {t("رجوع للسوق", "Back to marketplace")}
        </Link>

        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">{t("نوع الإعلان", "Listing type")}</h3>
          <div className="flex gap-3">
            {["sale", "rent"].map((opt) => (
              <button key={opt} type="button"
                onClick={() => update("listing_type", opt)}
                className={`flex-1 px-4 py-3 rounded-xl border transition-colors ${form.listing_type === opt ? "bg-yellow-500/15 border-yellow-500 text-yellow-400" : "bg-slate-900 border-slate-700 text-slate-300"}`}
                data-testid={`veh-type-${opt}`}>
                {opt === "sale" ? t("للبيع", "For Sale") : t("للإيجار", "For Rent")}
              </button>
            ))}
          </div>
          <input className={inputCls} placeholder={t("عنوان الإعلان", "Listing title")} value={form.title} onChange={(e) => update("title", e.target.value)} required data-testid="veh-title" />
          <input className={inputCls} type="number" placeholder={form.listing_type === "rent" ? t("السعر اليومي ($)", "Daily price ($)") : t("سعر البيع ($)", "Selling price ($)")} value={form.price} onChange={(e) => update("price", e.target.value)} required data-testid="veh-price" />
          {form.price && (
            <p className="text-xs text-slate-400 mt-1" data-testid="veh-price-hint">
              {t(
                `سعرك: $${Number(form.price).toLocaleString()} — سيظهر للعميل: $${(Number(form.price) * 1.05).toLocaleString(undefined, {maximumFractionDigits: 2})} (شامل 5% رسوم منصة عرب سند).`,
                `Your price: $${Number(form.price).toLocaleString()} — Customer sees: $${(Number(form.price) * 1.05).toLocaleString(undefined, {maximumFractionDigits: 2})} (includes 5% Arab Sanad platform fee).`
              )}
            </p>
          )}
        </div>

        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">{t("تفاصيل السيارة", "Vehicle details")}</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <select className={inputCls} value={form.brand} onChange={(e) => update("brand", e.target.value)} required data-testid="veh-brand">
              {VEHICLE_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <input className={inputCls} placeholder={t("الموديل", "Model")} value={form.model} onChange={(e) => update("model", e.target.value)} required data-testid="veh-model" />
            <input className={inputCls} type="number" placeholder={t("سنة الصنع", "Year")} value={form.year} onChange={(e) => update("year", e.target.value)} required data-testid="veh-year" />
            <input className={inputCls} placeholder={t("اللون", "Color")} value={form.color} onChange={(e) => update("color", e.target.value)} required data-testid="veh-color" />
            <input className={inputCls} type="number" placeholder={t("المسافة المقطوعة (ميل)", "Mileage (miles)")} value={form.mileage} onChange={(e) => update("mileage", e.target.value)} data-testid="veh-mileage" />
            <select className={inputCls} value={form.transmission} onChange={(e) => update("transmission", e.target.value)} data-testid="veh-transmission">
              {TRANSMISSIONS.map((tr) => <option key={tr.value} value={tr.value}>{language === "ar" ? tr.label_ar : tr.label_en}</option>)}
            </select>
            <select className={inputCls} value={form.fuel_type} onChange={(e) => update("fuel_type", e.target.value)} data-testid="veh-fuel">
              {FUEL_TYPES.map((f) => <option key={f.value} value={f.value}>{language === "ar" ? f.label_ar : f.label_en}</option>)}
            </select>
            <select className={inputCls} value={form.condition} onChange={(e) => update("condition", e.target.value)} data-testid="veh-condition">
              {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{language === "ar" ? c.label_ar : c.label_en}</option>)}
            </select>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">{t("الموقع", "Location")}</h3>
          <PlacesAutocompleteInput
            placeholder={t("ابحث في خرائط Google لملء الحقول تلقائياً", "Search Google to auto-fill")}
            types="(cities)"
            dataTestId="veh-place-search"
            onSelect={(d) => {
              if (d.state) update("state", d.state);
              if (d.city) update("city", d.city);
            }}
          />
          <div className="grid md:grid-cols-2 gap-4">
            <select className={inputCls} value={form.state} onChange={(e) => update("state", e.target.value)} required data-testid="veh-state">
              <option value="">{t("اختر الولاية", "Select State")}</option>
              {US_STATES.map((s) => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
            </select>
            <input className={inputCls} placeholder={t("المدينة", "City")} value={form.city} onChange={(e) => update("city", e.target.value)} required data-testid="veh-city" />
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">{t("الوصف والصور", "Description & images")}</h3>
          <textarea className={inputCls} rows={4} placeholder={t("وصف السيارة بالتفصيل", "Describe the car in detail")} value={form.description} onChange={(e) => update("description", e.target.value)} required data-testid="veh-description" />
          <ImageUploader value={form.images} onChange={(imgs) => update("images", imgs)} />
        </div>

        {/* Videos — unlimited, admin-reviewed before going live */}
        <div className="glass-card rounded-2xl p-5 space-y-3">
          <VideoUploader value={form.videos} onChange={(vids) => update("videos", vids)} />
        </div>

        <div className="glass-card rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">{t("معلومات التواصل", "Contact info")}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <input className={inputCls} placeholder={t("اسم المالك", "Owner name")} value={form.owner_name} onChange={(e) => update("owner_name", e.target.value)} required data-testid="veh-owner-name" />
            <input className={inputCls} placeholder={t("رقم الهاتف", "Phone")} value={form.phone} onChange={(e) => update("phone", e.target.value)} required data-testid="veh-phone" />
          </div>
        </div>

        {/* Terms & Conditions — required before publishing */}
        <div className="glass-card rounded-2xl p-5 border border-yellow-500/30 bg-yellow-500/5" data-testid="terms-section">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.accept_terms}
              onChange={(e) => update("accept_terms", e.target.checked)}
              className="mt-1 w-5 h-5 accent-yellow-500"
              data-testid="veh-accept-terms"
            />
            <span className="text-sm text-slate-200 leading-relaxed">
              {t(
                "أوافق على شروط وأحكام عرب سند. أُقرّ بأن السعر المعروض على العميل سيشمل نسبة منصة عرب سند، وأن المنصة تحتفظ بحقها في مراجعة وقبول أو رفض الإعلان قبل نشره.",
                "I accept Arab Sanad's Terms and Conditions. I acknowledge that the price shown to customers will include Arab Sanad's platform fee, and that Arab Sanad reserves the right to review, accept, or reject the listing before publishing."
              )}
            </span>
          </label>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={submitting || !form.accept_terms} className="btn-primary" data-testid="submit-vehicle-btn">
            <Save className="ml-2" size={18} />
            {submitting ? t("جاري النشر...", "Posting...") : t("نشر الإعلان", "Post Listing")}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  );
}
